import React, { createContext, useState, ReactNode } from 'react';
import { addDays, isValidDate } from '../constants/dates';
import logger from '../utils/logger';
import { getInitialDate, STORAGE_KEY } from '../utils/dateHelpers';

export interface DateContextType {
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  goToToday: () => void;
  isToday: (date?: string) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const DateContext = createContext<DateContextType | undefined>(undefined);

interface DateProviderProps {
  children: ReactNode;
}

export const DateProvider: React.FC<DateProviderProps> = ({ children }) => {
  const [selectedDate, setSelectedDateState] = useState<string>(getInitialDate);

  // 日付変更時にlocalStorageに保存（日付固定時は保存しない）
  const setSelectedDate = (date: string) => {
    if (isValidDate(date)) {
      setSelectedDateState(date);

      // VITE_FIXED_DATEが設定されている場合はLocalStorageに保存しない
      if (!import.meta.env.VITE_FIXED_DATE) {
        try {
          localStorage.setItem(STORAGE_KEY, date);
        } catch (error) {
          logger.warn('Failed to save date to localStorage:', { error });
        }
      }
    }
  };

  // 前日に移動
  const goToPreviousDay = () => {
    const previousDay = addDays(selectedDate, -1);
    setSelectedDate(previousDay);
  };

  // 翌日に移動
  const goToNextDay = () => {
    const nextDay = addDays(selectedDate, 1);
    setSelectedDate(nextDay);
  };

  // 今日（VITE_FIXED_DATEまたは実際の今日）に移動
  const goToToday = () => {
    const fixedDate = import.meta.env.VITE_FIXED_DATE;
    const today =
      fixedDate && isValidDate(fixedDate) ? fixedDate : new Date().toISOString().split('T')[0];
    setSelectedDate(today);
  };

  // 指定した日付（またはselectedDate）が今日（VITE_FIXED_DATEまたは実際の今日）かを判定
  const isToday = (date?: string) => {
    const targetDate = date || selectedDate;
    const fixedDate = import.meta.env.VITE_FIXED_DATE;
    const today =
      fixedDate && isValidDate(fixedDate) ? fixedDate : new Date().toISOString().split('T')[0];
    return targetDate === today;
  };

  const value: DateContextType = {
    selectedDate,
    setSelectedDate,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    isToday,
  };

  return <DateContext.Provider value={value}>{children}</DateContext.Provider>;
};
